import MainProject from '@/model/MainProject'
import { buildDefaultMainProject, type NewProjectResolution } from '@/model/buildDefaultMainProject'
import { buildDefaultVirtualResourceRoots } from '@/model/Resource/buildDefaultVirtualResourceRoots'
import {
  requestNewProjectRemoteMeta,
  recordCreateTracking,
  type NewProjectRemoteMeta,
} from '@/features/project-cloud/api'
import {
  projectOssSaveService,
  type ProjectOssSaveResult,
  type ProjectOssSaveProgress,
} from '@/features/project-cloud/ossSave'
import { ensureMallNewForProject } from '@/features/project-mall/runtime'
import { ensureProjectDefaultFont, normalizeProjectFontResources } from '@/features/project-font/runtime'
import { applyUiTemplatePackageToProject } from '@/features/ui-template/apply'
import { loadBuiltinUiTemplateBytes } from '@/features/ui-template/builtinCatalog'
import { parseUiTemplatePackageFromOuiqSource } from '@/features/ui-template/package'
import type { VirtualRoots } from '@/features/ui-template/package'
import { seedBuiltInVirtualResources } from '@/features/workspace/usecases/builtInResources'

export type WorkspaceCreationProgress = (percent: number, status: string) => void

function report(progress: WorkspaceCreationProgress | undefined, percent: number, status: string) {
  progress?.(percent, status)
}

type InitializeWorkspaceProjectParams = {
  project: MainProject
  resolution: NewProjectResolution
  templateId: string
  onProgress?: WorkspaceCreationProgress
  fontProgressPercent: number
  resourceProgressPercent: number
  resourceProgressText: string
}

/**
 * 初始化新工程的默认字体、虚拟资源树、模板资源与商城数据。
 * 云端新建和已发布工程恢复后的初始化都复用这套流程。
 */
async function initializeWorkspaceProject(
  params: InitializeWorkspaceProjectParams,
): Promise<VirtualRoots> {
  report(params.onProgress, params.fontProgressPercent, '同步默认字体…')
  await ensureProjectDefaultFont(params.project, true)

  report(params.onProgress, params.resourceProgressPercent, params.resourceProgressText)
  let roots = seedBuiltInVirtualResources({
    resolution: params.resolution,
    roots: buildDefaultVirtualResourceRoots(),
  })
  roots = await applySelectedTemplate({
    templateId: params.templateId,
    project: params.project,
    roots,
    onProgress: params.onProgress,
  })
  roots.font = (await normalizeProjectFontResources(params.project, roots.font)).root
  ensureMallNewForProject(params.project)
  return roots
}

async function applySelectedTemplate(params: {
  templateId: string
  project: MainProject
  roots: VirtualRoots
  onProgress?: WorkspaceCreationProgress
}): Promise<VirtualRoots> {
  const templateId = params.templateId.trim()
  if (!templateId) throw new Error('新建作品必须选择皮肤模板')

  report(params.onProgress, 58, '读取内置 ouiq 模板…')
  const bytes = await loadBuiltinUiTemplateBytes({
    templateId,
    dataVersion: params.project.projectConstant.dataVersion || params.project.dataVer,
    onProgress: (progress, status) => {
      report(params.onProgress, 58 + Math.round(progress * 0.14), status)
    },
  })

  report(params.onProgress, 73, '应用模板到工程…')
  return applyUiTemplatePackageToProject({
    project: params.project,
    roots: params.roots,
    template: parseUiTemplatePackageFromOuiqSource(bytes),
  }).roots
}

/**
 * 创建云端工程所需的完整初始数据。
 * 负责默认工程、远端 guid 申请、内置资源初始化、默认字体同步与首次 OSS 保存。
 */
export async function createCloudWorkspaceProject(params: {
  gameName: string
  resolution: NewProjectResolution
  templateId: string
  uid: number | string
  sessionId: string
  onProgress?: WorkspaceCreationProgress
  onSaveProgress?: (progress: ProjectOssSaveProgress) => void
}): Promise<{
  project: MainProject
  roots: VirtualRoots
  remoteMeta: NewProjectRemoteMeta
  saveResult: ProjectOssSaveResult
}> {
  report(params.onProgress, 18, '生成工程数据…')
  const project = buildDefaultMainProject(params.gameName, params.resolution)
  project.projectHeader.projectPath = params.gameName.trim() || ''
  project.projectHeader.UID = String(params.uid)
  project.projectHeader.Version = 0

  report(params.onProgress, 34, '请求服务端工程信息…')
  const remoteMeta = await requestNewProjectRemoteMeta({
    sessionId: params.sessionId,
    projName: params.gameName,
  })
  project.projectHeader.ID = remoteMeta.guid
  project.projectHeader.Time = remoteMeta.createTime
  project.projectConstant.gameVersion = 0

  const roots = await initializeWorkspaceProject({
    project,
    resolution: params.resolution,
    templateId: params.templateId,
    onProgress: params.onProgress,
    fontProgressPercent: 46,
    resourceProgressPercent: 54,
    resourceProgressText: '初始化虚拟资源管理器…',
  })

  report(params.onProgress, 74, '保存到 OSS…')
  const saveResult = await projectOssSaveService.save({
    project,
    virtualResourceRoots: roots,
    uid: params.uid,
    onProgress: params.onSaveProgress,
  })

  return {
    project,
    roots,
    remoteMeta,
    saveResult,
  }
}

/**
 * 统一封装“新建工程”埋点，失败时不阻断主流程。
 */
export function trackWorkspaceProjectCreate(params: {
  sessionId: string
  uid: number
  guid: string
  project?: MainProject | null
}): void {
  void recordCreateTracking(params).catch(() => {})
}
